// audio.ts — the M2 AUDIO LANE. A thin, scene-agnostic bus over Phaser's sound manager.
//
// Responsibilities:
//   - declare every audio asset by logical key -> fingerprinted URL (Vite ?url imports),
//   - queue them into a scene's loader (loadInto) so BootScene can preload once,
//   - play one-shot SFX / announcer VO with master volume + mute from settings.ts,
//   - run THREE music layers (menu / gameplay / tension) as always-on loops whose GAINS are
//     crossfaded by an "intensity" 0..1 — menu vs gameplay is a hard scene swap; the tension
//     layer rides ON TOP of gameplay as the timer/threat spikes,
//   - respect a persisted volume/mute setting (settings.ts) and re-apply it live.
//
// Determinism / headless safety: in the Playwright eyes harness there is no user gesture, so the
// WebAudio context stays SUSPENDED and every play() is a harmless no-op — captures never hang on
// audio and the M0/M1 gate is unaffected. All decode/network failures are swallowed; audio is
// strictly additive juice and must never break the playable slice.
import Phaser from 'phaser';
import { getSettings, onSettingsChange } from './settings';

// --- music --------------------------------------------------------------------
import musicMenuUrl from '../../assets/audio/music/music-menu-calm.mp3?url';
import musicGameplayUrl from '../../assets/audio/music/music-gameplay-drive.mp3?url';
import musicTensionUrl from '../../assets/audio/music/music-tension-layer.mp3?url';
// FACE & FURY S7b — the VILLAIN BASSLINE layer (BYT3's theme; 4th music layer, swells in when BYT3
// leads, routes through the S7 music sub-bus) + the TRIUMPHANT one-shot swell under the PERFECT GAUNTLET
// wordmark (fired on the SFX one-shot path alongside the perfectGauntlet sting).
import musicVillainUrl from '../../assets/audio/music/music-villain-layer.mp3?url';
import musicTriumphUrl from '../../assets/audio/music/music-perfect-triumph.mp3?url';
// --- sfx ----------------------------------------------------------------------
import sfxMunchUrl from '../../assets/audio/sfx/munch-correct.mp3?url';
import sfxMunchHiUrl from '../../assets/audio/sfx/munch-correct-hi.mp3?url';
import sfxWrongUrl from '../../assets/audio/sfx/wrong-buzz.mp3?url';
import sfxCombo1Url from '../../assets/audio/sfx/combo-up-1.mp3?url';
import sfxCombo2Url from '../../assets/audio/sfx/combo-up-2.mp3?url';
import sfxCombo3Url from '../../assets/audio/sfx/combo-up-3.mp3?url';
import sfxScanUrl from '../../assets/audio/sfx/scan.mp3?url';
import sfxHazardNearUrl from '../../assets/audio/sfx/hazard-near.mp3?url';
import sfxBoardClearUrl from '../../assets/audio/sfx/board-clear.mp3?url';
import sfxLifeLostUrl from '../../assets/audio/sfx/life-lost.mp3?url';
import sfxUiTapUrl from '../../assets/audio/sfx/ui-tap.mp3?url';
import sfxUiConfirmUrl from '../../assets/audio/sfx/ui-confirm.mp3?url';
import sfxCountdownUrl from '../../assets/audio/sfx/countdown.mp3?url';
import sfxGauntletUrl from '../../assets/audio/sfx/gauntlet-threaded.mp3?url';
import sfxLockUrl from '../../assets/audio/sfx/lock.mp3?url';
import sfxPerfectUrl from '../../assets/audio/sfx/perfect.mp3?url';
import sfxMoveStepUrl from '../../assets/audio/sfx/move-step.mp3?url';
// FACE & FURY S5 CRESCENDO — the signature-moment SFX. Three escalating chain risers (tier 1→3), a
// screen-consuming PERFECT GAUNTLET sting, and a photo-finish slow-mo-whoosh-into-snap.
import sfxChainRiser1Url from '../../assets/audio/sfx/sfx-chain-riser-1.mp3?url';
import sfxChainRiser2Url from '../../assets/audio/sfx/sfx-chain-riser-2.mp3?url';
import sfxChainRiser3Url from '../../assets/audio/sfx/sfx-chain-riser-3.mp3?url';
import sfxPerfectGauntletUrl from '../../assets/audio/sfx/sfx-perfect-gauntlet.mp3?url';
import sfxPhotoFinishUrl from '../../assets/audio/sfx/sfx-photo-finish.mp3?url';
// FACE & FURY S9 #10 — the threading EXHALE up-whoosh (fired on a held-breath release).
import sfxThreadExhaleUrl from '../../assets/audio/sfx/thread-exhale.mp3?url';
// FACE & FURY S9 #61 — the two highest-frequency UI verbs get their own voice (they used to share one
// uiTap): a two-tone settings toggle + a soft shared panel-open whoosh.
import sfxUiToggleUrl from '../../assets/audio/sfx/ui-toggle.mp3?url';
import sfxUiPanelOpenUrl from '../../assets/audio/sfx/ui-panel-open.mp3?url';
// --- vo (announcer) -----------------------------------------------------------
import voDeliciousUrl from '../../assets/audio/vo/vo-delicious.mp3?url';
import voThreadedUrl from '../../assets/audio/vo/vo-threaded-it.mp3?url';
import voPerfectBoardUrl from '../../assets/audio/vo/vo-perfect-board.mp3?url';
import voComboUrl from '../../assets/audio/vo/vo-combo.mp3?url';
import voSuperComboUrl from '../../assets/audio/vo/vo-super-combo.mp3?url';
import voUnstoppableUrl from '../../assets/audio/vo/vo-unstoppable.mp3?url';
import voTopicIntroUrl from '../../assets/audio/vo/vo-topic-intro.mp3?url';
import voGetReadyUrl from '../../assets/audio/vo/vo-get-ready.mp3?url';
import voYouWinUrl from '../../assets/audio/vo/vo-you-win.mp3?url';
import voGauntletChainUrl from '../../assets/audio/vo/vo-gauntlet-chain.mp3?url';
import voFlawlessUrl from '../../assets/audio/vo/vo-flawless.mp3?url';
// FACE & FURY S9 #43 — announcer TAKE-2 recordings (Liam, alternate delivery) for the highest-frequency
// callouts, so a chain storm never repeats one clip verbatim. Same voice/line, different read.
import voGauntletChain2Url from '../../assets/audio/vo/vo-gauntlet-chain-2.mp3?url';
import voCombo2Url from '../../assets/audio/vo/vo-combo-2.mp3?url';
// --- rival barks (BYT3 persona, 2026-07-02) — a SECOND voice lane under the announcer ------------
import barkStartUrl from '../../assets/audio/vo/rival/bark-start.mp3?url';
import barkCorrectUrl from '../../assets/audio/vo/rival/bark-correct.mp3?url';
import barkDecoyUrl from '../../assets/audio/vo/rival/bark-decoy.mp3?url';
import barkLeadUrl from '../../assets/audio/vo/rival/bark-lead.mp3?url';
import barkBehindUrl from '../../assets/audio/vo/rival/bark-behind.mp3?url';
import barkThreadedUrl from '../../assets/audio/vo/rival/bark-threaded.mp3?url';
import barkBoardWinUrl from '../../assets/audio/vo/rival/bark-board-win.mp3?url';
import barkBoardLossUrl from '../../assets/audio/vo/rival/bark-board-loss.mp3?url';
import barkMatchWinUrl from '../../assets/audio/vo/rival/bark-match-win.mp3?url';
import barkMatchLossUrl from '../../assets/audio/vo/rival/bark-match-loss.mp3?url';
// Second recorded take per bark (2026-07-02) — same voice/chain, alternate delivery. Gives every
// bark a 2-take pool so the rival never says the exact same line back-to-back (audio review: "No,
// no, no, no!" repeated verbatim in 50s read robotic). Paired take-2 caption lines live in rivalPersona.
import barkStart2Url from '../../assets/audio/vo/rival/bark-start-2.mp3?url';
import barkCorrect2Url from '../../assets/audio/vo/rival/bark-correct-2.mp3?url';
import barkDecoy2Url from '../../assets/audio/vo/rival/bark-decoy-2.mp3?url';
import barkLead2Url from '../../assets/audio/vo/rival/bark-lead-2.mp3?url';
import barkBehind2Url from '../../assets/audio/vo/rival/bark-behind-2.mp3?url';
import barkThreaded2Url from '../../assets/audio/vo/rival/bark-threaded-2.mp3?url';
import barkBoardWin2Url from '../../assets/audio/vo/rival/bark-board-win-2.mp3?url';
import barkBoardLoss2Url from '../../assets/audio/vo/rival/bark-board-loss-2.mp3?url';
import barkMatchWin2Url from '../../assets/audio/vo/rival/bark-match-win-2.mp3?url';
import barkMatchLoss2Url from '../../assets/audio/vo/rival/bark-match-loss-2.mp3?url';
// GHOST DUELS Slice D — BYT3-as-gatekeeper ladder VO. SINGLE takes (no 2-take pool): each is a
// once-per-moment gatekeeper line, not a repeatable in-board reaction, so no alternate delivery is needed.
import barkLadderIntroUrl from '../../assets/audio/vo/rival/bark-ladder-intro.mp3?url';
import barkRungWinUrl from '../../assets/audio/vo/rival/bark-rung-win.mp3?url';
import barkRungLossUrl from '../../assets/audio/vo/rival/bark-rung-loss.mp3?url';
import barkLadderClearUrl from '../../assets/audio/vo/rival/bark-ladder-clear.mp3?url';
// FACE & FURY S7 SONIC — the five crescendo / set-piece rival barks (the S5 OPTIONAL_BARK seam goes
// LIVE here). BYT3's cockiness cracks across the five peak moments: grudging alarm → rising panic →
// full meltdown → stunned disbelief → breathless. SINGLE takes (once-per-moment peaks, no 2-take pool).
import barkChain2Url from '../../assets/audio/vo/rival/bark-chain-2.mp3?url';
import barkChain3Url from '../../assets/audio/vo/rival/bark-chain-3.mp3?url';
import barkChain4Url from '../../assets/audio/vo/rival/bark-chain-4.mp3?url';
import barkPerfectUrl from '../../assets/audio/vo/rival/bark-perfect.mp3?url';
import barkPhotoFinishUrl from '../../assets/audio/vo/rival/bark-photo-finish.mp3?url';
// FACE & FURY S7b GRUDGE VO — the 20 STATIC (placeholder-free) relationship-aware grudge lines from
// rivalMemory.ts, recorded in BYT3's voice. They REPLACE the generic barkStart / barkMatchWin|Loss take
// when the deterministic grudge pick lands on a recorded line (see rivalMemory.grudgeLineClipKey + the
// OPTIONAL_BARK seam below); dynamic `{d}/{w}/{l}` lines stay caption-only over the generic clip.
// 9 board-START cold-opens:
import barkGrudgeFearThread1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-fear-thread-1.mp3?url';
import barkGrudgeFearThread2Url from '../../assets/audio/vo/rival/grudge/bark-grudge-fear-thread-2.mp3?url';
import barkGrudgeFearThread3Url from '../../assets/audio/vo/rival/grudge/bark-grudge-fear-thread-3.mp3?url';
import barkGrudgeAfterLoss1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-after-loss-1.mp3?url';
import barkGrudgeAfterLoss2Url from '../../assets/audio/vo/rival/grudge/bark-grudge-after-loss-2.mp3?url';
import barkGrudgeAfterLoss3Url from '../../assets/audio/vo/rival/grudge/bark-grudge-after-loss-3.mp3?url';
import barkGrudgeAfterWin1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-after-win-1.mp3?url';
import barkGrudgeAfterWin2Url from '../../assets/audio/vo/rival/grudge/bark-grudge-after-win-2.mp3?url';
import barkGrudgeAfterWin3Url from '../../assets/audio/vo/rival/grudge/bark-grudge-after-win-3.mp3?url';
// 11 match-END result lines:
import barkGrudgeResComeback1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-comeback-1.mp3?url';
import barkGrudgeResComeback2Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-comeback-2.mp3?url';
import barkGrudgeResWinBehind1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-win-behind-1.mp3?url';
import barkGrudgeResWinBehind2Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-win-behind-2.mp3?url';
import barkGrudgeResWinAgain1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-win-again-1.mp3?url';
import barkGrudgeResWinAgain2Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-win-again-2.mp3?url';
import barkGrudgeResRevenge1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-revenge-1.mp3?url';
import barkGrudgeResRevenge2Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-revenge-2.mp3?url';
import barkGrudgeResLossDom1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-loss-dom-1.mp3?url';
import barkGrudgeResDraw1Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-draw-1.mp3?url';
import barkGrudgeResDraw2Url from '../../assets/audio/vo/rival/grudge/bark-grudge-res-draw-2.mp3?url';

/** Logical music-layer keys. */
export type MusicKey = 'musicMenu' | 'musicGameplay' | 'musicTension' | 'musicVillain';
/** Logical one-shot SFX keys. */
export type SfxKey =
  | 'munch'
  | 'munchHi'
  | 'wrong'
  | 'combo1'
  | 'combo2'
  | 'combo3'
  | 'scan'
  | 'hazardNear'
  | 'boardClear'
  | 'lifeLost'
  | 'uiTap'
  | 'uiConfirm'
  | 'countdown'
  | 'gauntlet'
  | 'lock'
  | 'perfect'
  | 'moveStep'
  // FACE & FURY S5 — escalating GAUNTLET CHAIN risers (tier 1/2/3) + the two board-end set-piece stings.
  | 'chainRiser1'
  | 'chainRiser2'
  | 'chainRiser3'
  | 'perfectGauntlet'
  | 'photoFinish'
  // FACE & FURY S7b — the triumphant swell under the PERFECT GAUNTLET wordmark (music-family asset fired
  // on the one-shot path so it ducks the beds and clears the VO exactly like the set-piece sting).
  | 'perfectTriumph'
  // FACE & FURY S9 #10 — the threading EXHALE: a soft up-whoosh fired on the held-breath release so the
  // gauntlet payoff is HEARD (the muffle snaps open under it).
  | 'threadExhale'
  // FACE & FURY S9 #61 — dedicated voices for the two highest-frequency UI verbs (was one shared uiTap).
  | 'uiToggle'
  | 'uiPanelOpen';
/** Logical announcer-VO keys. */
export type VoKey =
  | 'voDelicious'
  | 'voThreaded'
  | 'voPerfectBoard'
  | 'voCombo'
  | 'voSuperCombo'
  | 'voUnstoppable'
  | 'voTopicIntro'
  | 'voGetReady'
  | 'voYouWin'
  | 'voGauntletChain'
  | 'voFlawless';
/** Rival (BYT3) bark keys — reactive one-liners from the opponent, never the announcer's job. */
export type BarkKey =
  | 'barkStart'
  | 'barkCorrect'
  | 'barkDecoy'
  | 'barkLead'
  | 'barkBehind'
  | 'barkThreaded'
  | 'barkBoardWin'
  | 'barkBoardLoss'
  | 'barkMatchWin'
  | 'barkMatchLoss'
  // GHOST DUELS Slice D — daily-ladder gatekeeper lines (single-take; see SINGLE_TAKE_BARKS below).
  | 'barkLadderIntro'
  | 'barkRungWin'
  | 'barkRungLoss'
  | 'barkLadderClear';

/** logicalKey -> Phaser cache key (we prefix to avoid collisions with future image keys). */
const AUDIO_KEY: Record<MusicKey | SfxKey | VoKey | BarkKey, string> = {
  musicMenu: 'aud-music-menu',
  musicGameplay: 'aud-music-gameplay',
  musicTension: 'aud-music-tension',
  musicVillain: 'aud-music-villain',
  munch: 'aud-munch',
  munchHi: 'aud-munch-hi',
  wrong: 'aud-wrong',
  combo1: 'aud-combo-1',
  combo2: 'aud-combo-2',
  combo3: 'aud-combo-3',
  scan: 'aud-scan',
  hazardNear: 'aud-hazard-near',
  boardClear: 'aud-board-clear',
  lifeLost: 'aud-life-lost',
  uiTap: 'aud-ui-tap',
  uiConfirm: 'aud-ui-confirm',
  countdown: 'aud-countdown',
  gauntlet: 'aud-gauntlet',
  lock: 'aud-lock',
  perfect: 'aud-perfect',
  moveStep: 'aud-move-step',
  chainRiser1: 'aud-chain-riser-1',
  chainRiser2: 'aud-chain-riser-2',
  chainRiser3: 'aud-chain-riser-3',
  perfectGauntlet: 'aud-perfect-gauntlet',
  photoFinish: 'aud-photo-finish',
  perfectTriumph: 'aud-perfect-triumph',
  threadExhale: 'aud-thread-exhale',
  uiToggle: 'aud-ui-toggle',
  uiPanelOpen: 'aud-ui-panel-open',
  voDelicious: 'aud-vo-delicious',
  voThreaded: 'aud-vo-threaded',
  voPerfectBoard: 'aud-vo-perfect-board',
  voCombo: 'aud-vo-combo',
  voSuperCombo: 'aud-vo-super-combo',
  voUnstoppable: 'aud-vo-unstoppable',
  voTopicIntro: 'aud-vo-topic-intro',
  voGetReady: 'aud-vo-get-ready',
  voYouWin: 'aud-vo-you-win',
  voGauntletChain: 'aud-vo-gauntlet-chain',
  voFlawless: 'aud-vo-flawless',
  barkStart: 'aud-bark-start',
  barkCorrect: 'aud-bark-correct',
  barkDecoy: 'aud-bark-decoy',
  barkLead: 'aud-bark-lead',
  barkBehind: 'aud-bark-behind',
  barkThreaded: 'aud-bark-threaded',
  barkBoardWin: 'aud-bark-board-win',
  barkBoardLoss: 'aud-bark-board-loss',
  barkMatchWin: 'aud-bark-match-win',
  barkMatchLoss: 'aud-bark-match-loss',
  barkLadderIntro: 'aud-bark-ladder-intro',
  barkRungWin: 'aud-bark-rung-win',
  barkRungLoss: 'aud-bark-rung-loss',
  barkLadderClear: 'aud-bark-ladder-clear',
};

const AUDIO_URL: Record<MusicKey | SfxKey | VoKey | BarkKey, string> = {
  musicMenu: musicMenuUrl,
  musicGameplay: musicGameplayUrl,
  musicTension: musicTensionUrl,
  musicVillain: musicVillainUrl,
  munch: sfxMunchUrl,
  munchHi: sfxMunchHiUrl,
  wrong: sfxWrongUrl,
  combo1: sfxCombo1Url,
  combo2: sfxCombo2Url,
  combo3: sfxCombo3Url,
  scan: sfxScanUrl,
  hazardNear: sfxHazardNearUrl,
  boardClear: sfxBoardClearUrl,
  lifeLost: sfxLifeLostUrl,
  uiTap: sfxUiTapUrl,
  uiConfirm: sfxUiConfirmUrl,
  countdown: sfxCountdownUrl,
  gauntlet: sfxGauntletUrl,
  lock: sfxLockUrl,
  perfect: sfxPerfectUrl,
  moveStep: sfxMoveStepUrl,
  chainRiser1: sfxChainRiser1Url,
  chainRiser2: sfxChainRiser2Url,
  chainRiser3: sfxChainRiser3Url,
  perfectGauntlet: sfxPerfectGauntletUrl,
  photoFinish: sfxPhotoFinishUrl,
  perfectTriumph: musicTriumphUrl,
  threadExhale: sfxThreadExhaleUrl,
  uiToggle: sfxUiToggleUrl,
  uiPanelOpen: sfxUiPanelOpenUrl,
  voDelicious: voDeliciousUrl,
  voThreaded: voThreadedUrl,
  voPerfectBoard: voPerfectBoardUrl,
  voCombo: voComboUrl,
  voSuperCombo: voSuperComboUrl,
  voUnstoppable: voUnstoppableUrl,
  voTopicIntro: voTopicIntroUrl,
  voGetReady: voGetReadyUrl,
  voYouWin: voYouWinUrl,
  voGauntletChain: voGauntletChainUrl,
  voFlawless: voFlawlessUrl,
  barkStart: barkStartUrl,
  barkCorrect: barkCorrectUrl,
  barkDecoy: barkDecoyUrl,
  barkLead: barkLeadUrl,
  barkBehind: barkBehindUrl,
  barkThreaded: barkThreadedUrl,
  barkBoardWin: barkBoardWinUrl,
  barkBoardLoss: barkBoardLossUrl,
  barkMatchWin: barkMatchWinUrl,
  barkMatchLoss: barkMatchLossUrl,
  barkLadderIntro: barkLadderIntroUrl,
  barkRungWin: barkRungWinUrl,
  barkRungLoss: barkRungLossUrl,
  barkLadderClear: barkLadderClearUrl,
};

// Bark TAKE-2 pool — a parallel second recording per BarkKey (cache key + URL). AUDIO_KEY/AUDIO_URL
// hold take 0; these hold take 1. playBark alternates so a repeated event never fires the same take
// twice in a row (the take index is returned so the caller captions the matching line). The Slice D
// gatekeeper keys are deliberately ABSENT here (single-take, once-per-moment) — playBark then always
// plays take 0 and captions BARK_LINE[key][0].
const BARK_TAKE2_KEY: Partial<Record<BarkKey, string>> = {
  barkStart: 'aud-bark-start-2',
  barkCorrect: 'aud-bark-correct-2',
  barkDecoy: 'aud-bark-decoy-2',
  barkLead: 'aud-bark-lead-2',
  barkBehind: 'aud-bark-behind-2',
  barkThreaded: 'aud-bark-threaded-2',
  barkBoardWin: 'aud-bark-board-win-2',
  barkBoardLoss: 'aud-bark-board-loss-2',
  barkMatchWin: 'aud-bark-match-win-2',
  barkMatchLoss: 'aud-bark-match-loss-2',
};
const BARK_TAKE2_URL: Partial<Record<BarkKey, string>> = {
  barkStart: barkStart2Url,
  barkCorrect: barkCorrect2Url,
  barkDecoy: barkDecoy2Url,
  barkLead: barkLead2Url,
  barkBehind: barkBehind2Url,
  barkThreaded: barkThreaded2Url,
  barkBoardWin: barkBoardWin2Url,
  barkBoardLoss: barkBoardLoss2Url,
  barkMatchWin: barkMatchWin2Url,
  barkMatchLoss: barkMatchLoss2Url,
};

// FACE & FURY S9 #43 — ANNOUNCER TAKE-2 pool. A parallel second recording per VoKey (same Liam voice /
// same line, alternate delivery), mirroring BARK_TAKE2 so a repeated callout never fires the same take
// twice in a row. Keys ABSENT here stay single-take (playVo then always plays take 0). The take-2 recording
// is registered by adding an import + a `${voKey}: url` entry below; every plumbing path (loadInto queue,
// playVo alternation) is already live, so a drop-in take needs only the two map rows here.
const VO_TAKE2_KEY: Partial<Record<VoKey, string>> = {
  voGauntletChain: 'aud-vo-gauntlet-chain-2',
  voCombo: 'aud-vo-combo-2',
};
const VO_TAKE2_URL: Partial<Record<VoKey, string>> = {
  voGauntletChain: voGauntletChain2Url,
  voCombo: voCombo2Url,
};

/**
 * OPTIONAL BARK SEAM (FACE & FURY S5 → S7). Reactive rival one-liners for the crescendo tiers + the
 * two board-end set-pieces. These are FORWARD SEAMS: S5 wires the call-sites (playOptionalBark) so
 * S7 only needs to drop a VO file + one URL-map entry. The URL map is EMPTY today → every
 * playOptionalBark is a silent no-op (registered only if the file exists, silent otherwise). Kept off
 * the strict BarkKey union so audio.ts never references a not-yet-recorded file (Vite would fail the
 * build on a missing `?url` import).
 */
export type OptionalBarkKey =
  | 'barkChain2'
  | 'barkChain3'
  | 'barkChain4'
  | 'barkPerfect'
  | 'barkPhotoFinish'
  // S7b grudge VO — 9 board-START cold-opens.
  | 'barkGrudgeFearThread1'
  | 'barkGrudgeFearThread2'
  | 'barkGrudgeFearThread3'
  | 'barkGrudgeAfterLoss1'
  | 'barkGrudgeAfterLoss2'
  | 'barkGrudgeAfterLoss3'
  | 'barkGrudgeAfterWin1'
  | 'barkGrudgeAfterWin2'
  | 'barkGrudgeAfterWin3'
  // S7b grudge VO — 11 match-END result lines.
  | 'barkGrudgeResComeback1'
  | 'barkGrudgeResComeback2'
  | 'barkGrudgeResWinBehind1'
  | 'barkGrudgeResWinBehind2'
  | 'barkGrudgeResWinAgain1'
  | 'barkGrudgeResWinAgain2'
  | 'barkGrudgeResRevenge1'
  | 'barkGrudgeResRevenge2'
  | 'barkGrudgeResLossDom1'
  | 'barkGrudgeResDraw1'
  | 'barkGrudgeResDraw2';
const OPTIONAL_BARK_CACHE_KEY: Record<OptionalBarkKey, string> = {
  barkChain2: 'aud-bark-chain-2',
  barkChain3: 'aud-bark-chain-3',
  barkChain4: 'aud-bark-chain-4',
  barkPerfect: 'aud-bark-perfect',
  barkPhotoFinish: 'aud-bark-photo-finish',
  barkGrudgeFearThread1: 'aud-bark-grudge-fear-thread-1',
  barkGrudgeFearThread2: 'aud-bark-grudge-fear-thread-2',
  barkGrudgeFearThread3: 'aud-bark-grudge-fear-thread-3',
  barkGrudgeAfterLoss1: 'aud-bark-grudge-after-loss-1',
  barkGrudgeAfterLoss2: 'aud-bark-grudge-after-loss-2',
  barkGrudgeAfterLoss3: 'aud-bark-grudge-after-loss-3',
  barkGrudgeAfterWin1: 'aud-bark-grudge-after-win-1',
  barkGrudgeAfterWin2: 'aud-bark-grudge-after-win-2',
  barkGrudgeAfterWin3: 'aud-bark-grudge-after-win-3',
  barkGrudgeResComeback1: 'aud-bark-grudge-res-comeback-1',
  barkGrudgeResComeback2: 'aud-bark-grudge-res-comeback-2',
  barkGrudgeResWinBehind1: 'aud-bark-grudge-res-win-behind-1',
  barkGrudgeResWinBehind2: 'aud-bark-grudge-res-win-behind-2',
  barkGrudgeResWinAgain1: 'aud-bark-grudge-res-win-again-1',
  barkGrudgeResWinAgain2: 'aud-bark-grudge-res-win-again-2',
  barkGrudgeResRevenge1: 'aud-bark-grudge-res-revenge-1',
  barkGrudgeResRevenge2: 'aud-bark-grudge-res-revenge-2',
  barkGrudgeResLossDom1: 'aud-bark-grudge-res-loss-dom-1',
  barkGrudgeResDraw1: 'aud-bark-grudge-res-draw-1',
  barkGrudgeResDraw2: 'aud-bark-grudge-res-draw-2',
};
// S7 WIRED (2026-07-04): the five crescendo / set-piece barks are recorded, accepted, and registered
// here — playOptionalBark now fires real VO from its real detection conditions (chain tiers 2/3/4+,
// PERFECT GAUNTLET, photo finish). Each is a single once-per-moment take (no take-2 pool).
const OPTIONAL_BARK_URL: Partial<Record<OptionalBarkKey, string>> = {
  barkChain2: barkChain2Url,
  barkChain3: barkChain3Url,
  barkChain4: barkChain4Url,
  barkPerfect: barkPerfectUrl,
  barkPhotoFinish: barkPhotoFinishUrl,
  // S7b grudge VO (accepted; measurement-in-family, ears listen pass = S9). A registered entry here is
  // what flips a grudge line from caption-only to VOICED — an absent key stays a silent no-op fallback.
  barkGrudgeFearThread1: barkGrudgeFearThread1Url,
  barkGrudgeFearThread2: barkGrudgeFearThread2Url,
  barkGrudgeFearThread3: barkGrudgeFearThread3Url,
  barkGrudgeAfterLoss1: barkGrudgeAfterLoss1Url,
  barkGrudgeAfterLoss2: barkGrudgeAfterLoss2Url,
  barkGrudgeAfterLoss3: barkGrudgeAfterLoss3Url,
  barkGrudgeAfterWin1: barkGrudgeAfterWin1Url,
  barkGrudgeAfterWin2: barkGrudgeAfterWin2Url,
  barkGrudgeAfterWin3: barkGrudgeAfterWin3Url,
  barkGrudgeResComeback1: barkGrudgeResComeback1Url,
  barkGrudgeResComeback2: barkGrudgeResComeback2Url,
  barkGrudgeResWinBehind1: barkGrudgeResWinBehind1Url,
  barkGrudgeResWinBehind2: barkGrudgeResWinBehind2Url,
  barkGrudgeResWinAgain1: barkGrudgeResWinAgain1Url,
  barkGrudgeResWinAgain2: barkGrudgeResWinAgain2Url,
  barkGrudgeResRevenge1: barkGrudgeResRevenge1Url,
  barkGrudgeResRevenge2: barkGrudgeResRevenge2Url,
  barkGrudgeResLossDom1: barkGrudgeResLossDom1Url,
  barkGrudgeResDraw1: barkGrudgeResDraw1Url,
  barkGrudgeResDraw2: barkGrudgeResDraw2Url,
};

// Per-bus base gains (relative to master) so VO sits above music, music sits under SFX.
const SFX_GAIN = 0.9;
const VO_GAIN = 1.0;
// FACE & FURY S9 #9 — rival-bark PARITY with the announcer. Was 0.82 (the rival sat a touch under the
// narrator); the S7b audit found BYT3 was the QUIETEST family in his OWN hero moments (buried under the
// beds+SFX both stages). He always yields to the announcer (playBark self-suppresses while a VO line owns
// the moment), so at parity he is never TALKING OVER the narrator — he is simply audible when it IS his
// beat. The ladder renorms cleanly: SFX 0.9 < BARK 1.0 = VO 1.0, beds 0.55.
const BARK_GAIN = 1.0;
const MUSIC_BED_GAIN = 0.55; // ceiling for any single music layer

// FACE & FURY S9 #8 — PERFECT GAUNTLET de-stack. The set-piece stacked 6 stems inside ~281 ms (sting +
// triumph swell + set-piece bark + announcer + boardClear + perfect) and pumped the master limiter. We
// STAGGER the swell + the rival punchline a hair BEHIND the impact transient and DUCK any non-hero
// one-shot ~3 dB for a short window, so the peak lands as impact→swell→voice instead of one summed wall.
const SETPIECE = {
  windowMs: 700, // after a perfectGauntlet impact, non-hero one-shots duck + the hero stems stagger
  swellStaggerMs: 120, // the triumphant swell blooms this long after the impact transient
  swellGain: 0.9, // and a hair softer so it supports the impact rather than fighting it
  barkStaggerMs: 240, // BYT3's set-piece punchline lands after the impact (impact→voice, not on top of it)
  duckGain: 0.71, // ~3 dB duck on any non-hero one-shot inside the window (declutters the peak)
} as const;
const SETPIECE_HERO: ReadonlySet<SfxKey> = new Set<SfxKey>(['perfectGauntlet', 'perfectTriumph', 'photoFinish']);

// FACE & FURY S9 #11 — final-5s countdown pitch-rise + low boom. The five ticks climb in pitch (0.9→1.25)
// so the clock reads as RISING panic, and the final tick layers the same asset pitched to a sub-boom (no
// new asset). Sequence-detected off inter-tick timing: an isolated countdown (the board-open get-ready)
// starts at step 0; a run of ticks ≤seqGapMs apart climbs the ladder, then resets when the gap reopens.
const COUNTDOWN = {
  seqGapMs: 1800, // a gap longer than this RESETS the pitch ladder (get-ready is isolated → step 0)
  rates: [0.9, 1.0, 1.1, 1.18, 1.25] as const, // five ticks (sec 5→1) climb in pitch
  boomStep: 4, // on the final (5th) tick, layer a low boom
  boomRate: 0.42, // the tick pitched way down = a sub 'boom' at zero (reuses the countdown asset)
  boomGain: 1.15, // a touch hotter so the boom lands under the last tick
} as const;

// FACE & FURY S9 #33 (heartbeat hook for Lane G1) — a low double-thud (lub-dub) the final-5s / last-life
// panic vignette can sync to. Reuses the 'lock' click pitched to a sub knock (no new asset); self-throttled
// so a per-frame caller can't machine-gun it. Lane AUDIO owns the SOUND; G1 emits HEARTBEAT_EVENT /
// PANIC_EVENT on the GAME-level emitter (survives scene swaps) and bindGameEvents() plays the heartbeat —
// the earlier "G1 calls playHeartbeat()" was aspirational: nothing subscribed, so the wire was dead (fixed).
const HEARTBEAT = {
  throttleMs: 620, // ≥ one beat interval — a per-frame caller collapses to ~1 beat/interval
  lubRate: 0.36,
  lubGain: 0.7,
  dubRate: 0.32,
  dubGain: 0.5,
  dubDelayMs: 165, // the 'dub' lands just after the 'lub' (Phaser play delay)
} as const;
// #33 event-name contract with Lane G1 (hudController.ts HEARTBEAT_EVENT / PANIC_EVENT). Emitted on the
// GAME-level emitter so a scene swap never drops them; kept as string literals on BOTH sides (audio.ts
// must not import a hudController symbol) — the two must stay in sync.
const HEARTBEAT_EVENT = 'trivia:heartbeat';
const PANIC_EVENT = 'trivia:panic';
// #33 heartbeat volume escalation: sec 5 (calm-ish) → sec 1 (loudest) as the payload counts down.
const HEARTBEAT_VOL = { base: 0.82, rise: 0.24 } as const;

// FACE & FURY S9 #10 — threading EXHALE. Releasing a held-breath gauntlet route (thread the CHOMP, or the
// fence expiring) fires a soft up-whoosh so the payoff is HEARD, not only felt, and the muffle now digs
// DEEPER (below) so the open has more contrast. Gated by a min-hold (no 1-frame flicker whoosh) + cooldown.
const THREAD_EXHALE = { minHoldMs: 170, cooldownMs: 850, gain: 0.6 } as const;

// FACE & FURY S9 #35 — VILLAIN pulse-on-rival-score. Each time BYT3 scores, his bassline layer gets a
// short additive swell ON TOP of the standing lead-margin presence, so the villain is audible on every
// rival point — not only in a blowout. Decays back to the margin-driven presence over releaseMs.
const VILLAIN_PULSE = { strength: 0.5, holdMs: 120, releaseMs: 640 } as const;

// FACE & FURY S9 #43 — announcer SAME-LINE cooldown. The audit caught vo-gauntlet-chain firing 22× verbatim
// in one session. Beyond the global anti-stack gap, a given announcer LINE cannot re-fire inside this
// window, so a chain storm spaces its callouts instead of machine-gunning one clip.
const VO_SAME_LINE_COOLDOWN_MS = 3500;

// --- FACE & FURY S7 — ADAPTIVE MUSIC DIRECTOR tuning (renderer-only presentation) ------------------
// The director subscribes to per-frame sim STATE (renderer reads only) and drives two reactive music
// behaviors through a MUSIC-ONLY WebAudio sub-bus (low-pass + duck gain) that SFX/VO never touch — the
// CHOMP payoff always stays bright. All transitions are AudioParam ramps (setTargetAtTime) = no zipper.
const MUSIC_LOWPASS_OPEN_HZ = 20000; // transparent — no muffle (held-breath released)
// FACE & FURY S9 #10 — the muffle now digs DEEPER (was 620 Hz / 0.8) so the RELEASE has more contrast: the
// threading exhale whoosh snaps the music from a low "underwater" hold back to fully open — the payoff you
// hear, not just feel. Paired with the exhale one-shot fired on the held-breath release edge (setHeldBreath).
const MUSIC_LOWPASS_MUFFLE_HZ = 470; // held-breath: music goes "underwater" while routing an armed decoy
const MUSIC_BREATH_DUCK = 0.66; // held-breath: the music sub-bus dips ~3.6 dB so the tension really reads
const MUSIC_LIFT_TENSION_FLOOR = 0.72; // combo/chain LIFT: floor the tension-layer crossfade this high
const MUSIC_VILLAIN_MAX = 0.7; // villain layer ceiling as a fraction of MUSIC_BED_GAIN (BYT3 clearly ahead)

type WebAudioSound = Phaser.Sound.WebAudioSound;

/**
 * Process-wide audio bus. Created once and shared across scenes via the singleton accessor. It binds
 * to a Phaser.Sound.BaseSoundManager (taken from whichever scene preloaded the files).
 */
class AudioBus {
  private sound: Phaser.Sound.BaseSoundManager | null = null;
  private layers = new Map<MusicKey, Phaser.Sound.BaseSound>();
  private layerTargetGain = new Map<MusicKey, number>(); // intensity-resolved target (pre-master)
  private mode: 'menu' | 'gameplay' | 'none' = 'none';
  private intensity = 0; // 0..1, drives gameplay<->tension blend
  private unsub: (() => void) | null = null;
  private lastVoAt = 0; // throttle announcer so lines don't stack on a combo storm
  // Real end time of the current announcer line (now + its decoded duration). Unlike the fixed
  // 1600ms yield window, this tracks the ACTUAL line length so a deferred rival bark can wait for a
  // long callout (e.g. GAUNTLET CHAIN) to truly finish before it speaks — the crescendo slow-mo can
  // otherwise stretch a scene-time bark delay past the fixed window while the line is still playing.
  private voBusyUntilMs = -Infinity;
  // DUCKING (first-listen review 2026-07-01: VO + fanfare stacking over the beds clipped the master):
  // music layers dip while a VO line / big fanfare owns the moment, then recover. duckLevel is the
  // smoothed multiplier tickFade applies to the music master; fast attack, ~0.5s release.
  private duckLevel = 1;
  private duckAmount = 1; // target multiplier while held (0.62 ≈ -4.2 dB for VO, 0.7 ≈ -3.1 dB for fanfares)
  private duckHoldUntilMs = 0;
  // #8 PERFECT GAUNTLET de-stack window (set on a perfectGauntlet impact; staggers/ducks the rest).
  private setPieceUntilMs = 0;
  // #11 countdown pitch ladder: which step of the 5-tick rise we're on + when the last tick fired.
  private countdownStep = -1;
  private lastCountdownMs = -Infinity;
  // #33 heartbeat throttle (G1's panic vignette hook).
  private lastHeartbeatMs = -Infinity;
  // #33 game-level heartbeat/panic wire: bound ONCE (the game emitter is shared across scenes), a live
  // panic flag, and a DEV-only fire counter so the harness can PROVE the event→sound wire (was dead).
  private gameEventsBound = false;
  private panicActive = false;
  private heartbeatFires = 0;
  // #10 held-breath timing: when the current muffle started + when we last exhaled (whoosh cooldown).
  private heldBreathSinceMs = 0;
  private lastExhaleMs = -Infinity;
  // #43 per-line announcer cooldown + take memory (same-line spacing + no-repeat take pick).
  private lastVoKeyAt = new Map<VoKey, number>();
  private lastVoTake = new Map<VoKey, number>();
  // #35 villain pulse-on-rival-score: a decaying additive swell above the standing villain presence.
  private villainPulse = 0;
  private villainPulseUntilMs = 0;

  // --- S7 ADAPTIVE MUSIC DIRECTOR: a MUSIC-ONLY sub-bus (low-pass + duck gain) fed by the layers ----
  // Each music layer's output is rerouted from the master into `musicFilter → musicBusGain → master`,
  // so the held-breath low-pass hits MUSIC ONLY (SFX/VO/barks stay bright — the release is the payoff).
  // musicBusReady=false (no WebAudio context / build failed) makes every director call a silent no-op.
  private musicFilter: BiquadFilterNode | null = null;
  private musicBusGain: GainNode | null = null;
  private musicBusReady = false;
  private routedLayers = new Set<MusicKey>(); // layers already rerouted through the sub-bus
  private heldBreath = false; // armed-traversal held-breath (muffle) state
  private musicLift = 0; // 0..1 combo/chain tension LIFT above the intensity mapping
  private villainPresence = 0; // 0..1 BYT3-lead → VILLAIN bassline swell (fed by setVillainPresence)

  /** Queue every audio file into a scene's loader. Safe to call once (BootScene). */
  loadInto(scene: Phaser.Scene): void {
    for (const k of Object.keys(AUDIO_URL) as (MusicKey | SfxKey | VoKey | BarkKey)[]) {
      if (!scene.cache.audio.exists(AUDIO_KEY[k])) {
        scene.load.audio(AUDIO_KEY[k], AUDIO_URL[k]);
      }
    }
    // Take-2 bark pool (parallel second recordings; single-take gatekeeper keys are absent → skipped).
    for (const k of Object.keys(BARK_TAKE2_URL) as BarkKey[]) {
      const cacheKey = BARK_TAKE2_KEY[k];
      const url = BARK_TAKE2_URL[k];
      if (cacheKey && url && !scene.cache.audio.exists(cacheKey)) {
        scene.load.audio(cacheKey, url);
      }
    }
    // Take-2 announcer pool (#43) — same shape as the bark pool; keys without a take-2 are skipped.
    for (const k of Object.keys(VO_TAKE2_URL) as VoKey[]) {
      const cacheKey = VO_TAKE2_KEY[k];
      const url = VO_TAKE2_URL[k];
      if (cacheKey && url && !scene.cache.audio.exists(cacheKey)) {
        scene.load.audio(cacheKey, url);
      }
    }
    // Optional S5→S7 crescendo/set-piece barks (URL map empty today → nothing queued; a no-op).
    for (const k of Object.keys(OPTIONAL_BARK_URL) as OptionalBarkKey[]) {
      const url = OPTIONAL_BARK_URL[k];
      if (url && !scene.cache.audio.exists(OPTIONAL_BARK_CACHE_KEY[k])) {
        scene.load.audio(OPTIONAL_BARK_CACHE_KEY[k], url);
      }
    }
  }

  /** Bind the bus to a sound manager (the game's, taken from any live scene). Idempotent per scene. */
  attach(scene: Phaser.Scene): void {
    if (!this.sound) {
      this.sound = scene.sound as Phaser.Sound.BaseSoundManager;
      this.applyMasterVolume();
      this.unsub = onSettingsChange(() => this.applyMasterVolume());
      // Master brickwall-ish limiter BEFORE the tee, so recordings capture the post-limiter mix a
      // player actually hears (first-listen review: VO+riser+fanfare stacks pushed true peaks past
      // 0 dBFS — audible clipping on the reward moment).
      installMasterLimiter(this.sound);
      // Capture-harness-only (?audiocap=1): expose window.__audiocap so the gameplay-clip tool can
      // record the REAL master mix in-page. No-op on every normal boot (param absent).
      if (captureTeeRequested()) installCaptureTee(this.sound);
      // DEV-only S7 evidence hooks: expose the adaptive music-director state (so the capture harness can
      // log real filter/gain/target transitions) and a VO cue (so it can make the announcer busy on cue,
      // to reproduce the cold-open announcer-yield race deterministically). `import.meta.env.DEV` is
      // statically false in a prod build, so Vite tree-shakes this whole block out of the shipped bundle
      // (zero player surface).
      if (import.meta.env.DEV) {
        try {
          const w = window as unknown as {
            __musicdir?: () => unknown;
            __audiocue?: (k: string) => void;
            __audiodbg?: () => unknown;
          };
          w.__musicdir = () => this.musicDirectorState();
          w.__audiocue = (k: string) => this.playVo(k as VoKey);
          // #33 proof hook: the harness drives the final-5s (cheat 'tension') and asserts heartbeats>0,
          // proving the once-dead HEARTBEAT_EVENT wire now reaches playHeartbeat.
          w.__audiodbg = () => ({ heartbeats: this.heartbeatFires, panicActive: this.panicActive, gameEventsBound: this.gameEventsBound });
        } catch {
          /* no window (tests) */
        }
      }
      // #33 heartbeat/panic wire (Lane G1 → Lane AUDIO). Bound to the GAME-level emitter so it survives
      // Menu→Game→Result swaps; guarded to bind exactly once for the app-lifetime singleton bus.
      this.bindGameEvents(scene.game);
    }
    // Drive the music crossfade on whatever scene is currently alive. Every scene that uses audio
    // re-binds its own UPDATE handler (removed on its own SHUTDOWN), so the ticker naturally follows
    // the active scene across Menu -> Game -> Result. off() first makes the bind idempotent if the
    // same scene attaches twice.
    scene.events.off(Phaser.Scenes.Events.UPDATE, this.tickFade, this);
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.tickFade, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      scene.events.off(Phaser.Scenes.Events.UPDATE, this.tickFade, this);
    });
  }

  /**
   * #33 heartbeat/panic wire. Lane G1 (hudController) emits HEARTBEAT_EVENT (payload = whole seconds
   * remaining, 5..1) once per second across the final five, and PANIC_EVENT (bool) on the last-life /
   * final-quarter tension edge. Lane AUDIO owns the SOUND; this binds ONCE to the GAME-level emitter
   * (shared across scenes, so it survives Menu→Game→Result) and plays the lub-dub, subtly hotter as the
   * clock runs down. playHeartbeat is self-throttled, so a bad emitter can never machine-gun it.
   */
  private bindGameEvents(game: Phaser.Game): void {
    if (this.gameEventsBound || !game?.events) return;
    this.gameEventsBound = true;
    game.events.on(HEARTBEAT_EVENT, (sec: unknown) => {
      const s = typeof sec === 'number' ? sec : 5;
      const urgency = Phaser.Math.Clamp((5 - s) / 4, 0, 1); // 0 at "5" → 1 at "1"
      this.playHeartbeat({ volume: HEARTBEAT_VOL.base + HEARTBEAT_VOL.rise * urgency });
    });
    game.events.on(PANIC_EVENT, (on: unknown) => {
      this.panicActive = !!on;
      // On the panic-clear edge, reset the throttle so the NEXT panic entry (e.g. last life on a fresh
      // board) beats immediately instead of waiting out a stale interval.
      if (!on) this.lastHeartbeatMs = -Infinity;
    });
  }

  private get masterVolume(): number {
    const s = getSettings();
    return s.muted ? 0 : s.volume;
  }

  /** Per-bus multipliers (master is applied separately) — M3 split mixer. */
  private get sfxBusMul(): number {
    return getSettings().sfxVolume;
  }
  private get musicBusMul(): number {
    return getSettings().musicVolume;
  }

  private applyMasterVolume(): void {
    if (!this.sound) return;
    try {
      // BaseSoundManager.volume scales everything; we keep per-sound gains relative to this.
      this.sound.volume = this.masterVolume;
    } catch {
      /* NoAudio manager — ignore. */
    }
  }

  // --- SFX / VO ---------------------------------------------------------------
  playSfx(key: SfxKey, opts: { volume?: number; rate?: number } = {}): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    // #11 final-5s countdown pitch-rise + low boom. Sequence-detected off inter-tick timing: an isolated
    // countdown (the board-open get-ready) starts at step 0; a run of ticks ≤seqGapMs apart climbs the
    // ladder (0.9→1.25) and the final tick layers the same asset pitched to a sub-boom — the "zero" hit.
    if (key === 'countdown') {
      this.countdownStep = now - this.lastCountdownMs > COUNTDOWN.seqGapMs ? 0 : Math.min(this.countdownStep + 1, COUNTDOWN.rates.length - 1);
      this.lastCountdownMs = now;
      const base = (opts.volume ?? 1) * SFX_GAIN * this.sfxBusMul;
      this.playOneShot(AUDIO_KEY.countdown, base, (opts.rate ?? 1) * COUNTDOWN.rates[this.countdownStep]);
      if (this.countdownStep >= COUNTDOWN.boomStep) this.playOneShot(AUDIO_KEY.countdown, base * COUNTDOWN.boomGain, COUNTDOWN.boomRate);
      return;
    }
    // #8 a perfectGauntlet impact opens the de-stack window: the triumph swell + the rival punchline
    // stagger BEHIND the impact, and any non-hero one-shot ducks ~3 dB across the window.
    if (key === 'perfectGauntlet') this.setPieceUntilMs = now + SETPIECE.windowMs;
    // Big set-piece one-shots own the moment: dip the music beds ~3 dB under them (review fix #2).
    if (key === 'boardClear' || key === 'perfect') this.duck(0.7, 1100);
    // FACE & FURY S5: the screen-consuming set-piece stings duck harder (~4 dB) and hold through the
    // wordmark/tag beat; the escalating chain risers get the same 3 dB tuck as the legacy riser.
    else if (key === 'perfectGauntlet') this.duck(0.6, 2000);
    // S7b triumphant swell: the musical sustain under the PERFECT GAUNTLET wordmark — same hard 2s duck as
    // the sting it rides beneath, so the beds (incl. the villain layer) drop and the VO stays clear above.
    else if (key === 'perfectTriumph') this.duck(0.6, 2000);
    else if (key === 'photoFinish') this.duck(0.62, 1400);
    else if (key === 'chainRiser1' || key === 'chainRiser2' || key === 'chainRiser3') this.duck(0.7, 900);
    let volume = (opts.volume ?? 1) * SFX_GAIN * this.sfxBusMul;
    let delaySec = 0;
    if (key === 'perfectTriumph') {
      delaySec = SETPIECE.swellStaggerMs / 1000; // bloom the swell a hair after the impact transient
      volume *= SETPIECE.swellGain; // and a touch softer so it supports, not fights, the impact
    } else if (now < this.setPieceUntilMs && !SETPIECE_HERO.has(key)) {
      volume *= SETPIECE.duckGain; // duck non-hero one-shots under the set-piece peak (#8)
    }
    this.playOneShot(AUDIO_KEY[key], volume, opts.rate ?? 1, delaySec);
  }

  /**
   * #33 panic heartbeat (Lane G1 hook). A low double-thud (lub-dub) the final-5s / last-life vignette can
   * sync to: reuses the 'lock' click pitched to a sub knock (no new asset), the 'dub' scheduled just after
   * the 'lub' via WebAudio play-delay. Self-throttled so a per-frame caller collapses to ~one beat/interval.
   * Lane AUDIO owns the sound; G1 calls this from the escalation. No-op while muted (playOneShot guards).
   */
  playHeartbeat(opts: { volume?: number } = {}): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.lastHeartbeatMs < HEARTBEAT.throttleMs) return;
    this.lastHeartbeatMs = now;
    this.heartbeatFires++; // #33 proof counter (read via window.__audiodbg in DEV)
    const base = (opts.volume ?? 1) * SFX_GAIN * this.sfxBusMul;
    this.playOneShot(AUDIO_KEY.lock, base * HEARTBEAT.lubGain, HEARTBEAT.lubRate);
    this.playOneShot(AUDIO_KEY.lock, base * HEARTBEAT.dubGain, HEARTBEAT.dubRate, HEARTBEAT.dubDelayMs / 1000);
  }

  /**
   * #61 settings toggle — the two-tone switch (was one generic uiTap on every control). A subtle pitch
   * split reinforces the state on top of the asset's own two-tone: ON at natural pitch, OFF a touch lower.
   */
  playToggle(on: boolean): void {
    this.playSfx('uiToggle', { volume: 0.7, rate: on ? 1.0 : 0.92 });
  }

  /** #61 panel-open whoosh — the soft shared transition cue for opening a modal/panel (deliberately subtle). */
  playPanelOpen(): void {
    this.playSfx('uiPanelOpen', { volume: 0.6 });
  }

  /** Announcer VO, throttled so a combo run doesn't talk over itself. */
  playVo(key: VoKey, opts: { volume?: number; minGapMs?: number } = {}): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const gap = opts.minGapMs ?? 900;
    if (now - this.lastVoAt < gap) return;
    // #43 same-line cooldown: a given callout can't re-fire inside VO_SAME_LINE_COOLDOWN_MS, so a chain
    // storm spaces its lines instead of repeating one clip verbatim (audit: vo-gauntlet-chain ×22).
    if (now - (this.lastVoKeyAt.get(key) ?? -Infinity) < VO_SAME_LINE_COOLDOWN_MS) return;
    this.lastVoAt = now;
    this.lastVoKeyAt.set(key, now);
    // #43 no-repeat take pick: alternate take 0/1 when a take-2 is registered (mirrors playBark); a key
    // with no take-2 always plays take 0.
    let cacheKey = AUDIO_KEY[key];
    if (VO_TAKE2_KEY[key] !== undefined) {
      const prev = this.lastVoTake.get(key);
      const take = prev === undefined ? (Math.random() < 0.5 ? 0 : 1) : prev ^ 1;
      this.lastVoTake.set(key, take);
      if (take === 1) cacheKey = VO_TAKE2_KEY[key] ?? AUDIO_KEY[key];
    }
    // Stamp the line's real end so announcerBusy() can defer a rival bark past a LONG callout.
    this.voBusyUntilMs = now + this.audioDurationMs(cacheKey);
    this.duck(0.62, 1500); // announcer owns the moment — music dips ~4 dB, recovers after the line
    // VO rides on the SFX bus multiplier so a "mute music, keep effects" mix keeps the announcer.
    this.playOneShot(cacheKey, (opts.volume ?? 1) * VO_GAIN * this.sfxBusMul, 1);
  }

  /**
   * Is the announcer lane still speaking? True until the current VO line's REAL end time. A deferred
   * rival bark (BYT3's threaded snark) polls this and reschedules rather than firing on top of a
   * callout — the fixed 1600ms yield window can be out-run by the crescendo slow-mo, this can't.
   * Falls back to false (bark may proceed) when no line is active.
   */
  announcerBusy(): boolean {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return now < this.voBusyUntilMs;
  }

  /** Decoded length (ms) of a cached clip; conservative ~1.6s fallback if the buffer isn't ready. */
  private audioDurationMs(cacheKey: string): number {
    try {
      const buf = this.sound?.game.cache.audio.get(cacheKey) as { duration?: number } | undefined;
      if (buf && typeof buf.duration === 'number' && buf.duration > 0) return buf.duration * 1000;
    } catch {
      /* cache miss / NoAudio — use the fallback */
    }
    return 1600;
  }

  // -Infinity, NOT 0: with 0 the 4s self-throttle treats page-load as "a bark just played" and
  // silently swallows any bark in the first 4s — which is exactly when the board-0 opener fires.
  private lastBarkAt = -Infinity; // rival self-throttle — it must never chatter
  private lastBarkTake = new Map<BarkKey, number>(); // per-key take memory — never repeat a take back-to-back

  /**
   * Rival (BYT3) bark — the SECOND voice lane. Yields to the announcer (skips if a VO line fired
   * in the last 1.6s and does NOT block future announcer lines), self-throttles hard (default
   * 4s) so the rival reads as a player reacting, not a commentator. Lighter/shorter duck than VO.
   *
   * Returns the TAKE INDEX played (0 or 1) so the caller can caption the matching line, or -1 when
   * the bark was suppressed (announcer owns the moment / self-throttle). Each key has a 2-take pool;
   * we always pick the take that is NOT the one it last used, so a repeated event alternates delivery.
   */
  playBark(key: BarkKey, opts: { volume?: number; minGapMs?: number } = {}): number {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.lastVoAt < 1600) return -1; // announcer owns the moment — the rival waits
    if (now - this.lastBarkAt < (opts.minGapMs ?? 4000)) return -1;
    this.lastBarkAt = now;
    // No-repeat take pick: flip off the last take for this key; first use is random-but-fixed. A
    // single-take bark (no take-2 registered, e.g. the Slice D gatekeeper lines) always plays take 0.
    let take = 0;
    if (BARK_TAKE2_KEY[key] !== undefined) {
      const prev = this.lastBarkTake.get(key);
      take = prev === undefined ? (Math.random() < 0.5 ? 0 : 1) : prev ^ 1;
      this.lastBarkTake.set(key, take);
    }
    const cacheKey = take === 1 ? (BARK_TAKE2_KEY[key] ?? AUDIO_KEY[key]) : AUDIO_KEY[key];
    this.duck(0.72, 800);
    // Rides the SFX bus multiplier like the announcer: "mute music, keep effects" keeps the rival.
    this.playOneShot(cacheKey, (opts.volume ?? 1) * BARK_GAIN * this.sfxBusMul, 1);
    // >=0 == the bark line FIRED (it may still be silent under mute — callers use this for the
    // speech caption, which should show even when audio is muted: it's the accessibility layer).
    return take;
  }

  /**
   * OPTIONAL crescendo/set-piece bark (S5 seam; S7 fills the VO). Silent no-op unless the file has
   * been registered (OPTIONAL_BARK_URL non-empty AND decoded) — so wiring the call-sites now costs
   * nothing until a real recording ships. Yields to the announcer and self-throttles exactly like a
   * normal bark. Returns true if it fired (for a future caption), false when suppressed / not present.
   */
  playOptionalBark(key: OptionalBarkKey, opts: { volume?: number; minGapMs?: number; setPiece?: boolean } = {}): boolean {
    const cacheKey = OPTIONAL_BARK_CACHE_KEY[key];
    if (!OPTIONAL_BARK_URL[key]) return false; // no VO file registered yet → silent no-op
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    // SET-PIECE barks (PERFECT GAUNTLET / PHOTO FINISH) are the once-per-board HERO punchlines: they must
    // land even though a chain bark spoke seconds earlier, so they BYPASS the announcer-yield + self-
    // throttle (they still stamp lastBarkAt so nothing chatters AFTER them). Chain barks pass through the
    // normal gate but with a shorter floor so a fast 2→3→4 chain can still escalate the panic in voice.
    if (!opts.setPiece) {
      if (now - this.lastVoAt < 1600) return false; // announcer owns the moment — the rival waits
      if (now - this.lastBarkAt < (opts.minGapMs ?? 4000)) return false; // rival self-throttle
    }
    this.lastBarkAt = now;
    this.duck(0.72, 800);
    // #8 de-stack: a set-piece punchline inside the perfectGauntlet window lands AFTER the impact transient
    // (impact→voice), so the rival's line reads instead of summing into the sting's peak.
    const delaySec = opts.setPiece && now < this.setPieceUntilMs ? SETPIECE.barkStaggerMs / 1000 : 0;
    this.playOneShot(cacheKey, (opts.volume ?? 1) * BARK_GAIN * this.sfxBusMul, 1, delaySec);
    return true;
  }

  /**
   * Is a recorded VO clip registered for this optional bark key? Lets a caller choose the VOICE path
   * (a recorded grudge line replacing the generic take) vs the caption-only fallback WITHOUT trying a
   * play first — so a suppressed-by-throttle result can't be confused with a not-recorded one.
   */
  hasOptionalBark(key: OptionalBarkKey): boolean {
    return !!OPTIONAL_BARK_URL[key];
  }

  /** Hold the music beds at `amount` (multiplier) for `holdMs`; tickFade eases in/out. */
  private duck(amount: number, holdMs: number): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    this.duckAmount = Math.min(amount, this.duckHoldUntilMs > now ? this.duckAmount : 1);
    this.duckHoldUntilMs = Math.max(this.duckHoldUntilMs, now + holdMs);
  }

  private playOneShot(cacheKey: string, volume: number, rate: number, delaySec = 0): void {
    if (!this.sound) return;
    if (this.masterVolume <= 0) return; // muted — skip the WebAudio churn entirely
    try {
      if (!this.sound.game.cache.audio.exists(cacheKey)) {
        return; // not loaded (decode failed / not preloaded) — skip silently
      }
      // play() returns false on the NoAudio manager; both are safe. `delay` (s) schedules the WebAudio
      // start later — used by the #8 set-piece stagger + the #33 heartbeat 'dub' (0 = play now).
      this.sound.play(cacheKey, delaySec > 0 ? { volume, rate, delay: delaySec } : { volume, rate });
    } catch {
      /* decode/context error — audio is additive, never fatal. */
    }
  }

  // --- music layers -----------------------------------------------------------
  /**
   * Match-continuity dim: Result screens duck the gameplay beds (×0.4) instead of stopping them, so
   * the track keeps advancing across a 5-board match (stopping restarted it at 0:00 every board and
   * fast players never heard past the intro). Cleared by every mode switch + GameScene re-entry.
   */
  private bedDim = false;
  setBedDim(on: boolean): void {
    this.bedDim = on;
  }

  /** Switch to the menu bed (single calm loop). */
  playMenuMusic(): void {
    this.bedDim = false;
    this.resetMusicDirector(); // no held-breath / lift carries into the menu
    if (this.mode === 'menu') return;
    this.mode = 'menu';
    this.ensureLayer('musicMenu');
    this.stopLayer('musicGameplay');
    this.stopLayer('musicTension');
    this.stopLayer('musicVillain');
    this.layerTargetGain.set('musicMenu', MUSIC_BED_GAIN);
  }

  /** Switch to the gameplay bed; the tension layer rides on top, gain driven by setIntensity(). */
  playGameplayMusic(): void {
    this.bedDim = false; // a live board always gets the full bed (mode-guard below keeps it running across boards)
    this.resetMusicDirector(); // fresh board starts breath-open, lift-clear (director re-establishes per frame)
    if (this.mode === 'gameplay') return;
    this.mode = 'gameplay';
    this.stopLayer('musicMenu');
    this.ensureLayer('musicGameplay');
    this.ensureLayer('musicTension');
    this.ensureLayer('musicVillain'); // 4th layer — starts silent, swells in via setVillainPresence
    this.intensity = 0;
    this.refreshGameplayTargets();
  }

  /** Stop all music (match end / quit — between-board results use setBedDim instead). */
  stopMusic(): void {
    this.bedDim = false;
    this.resetMusicDirector();
    this.mode = 'none';
    for (const k of this.layers.keys()) this.layerTargetGain.set(k, 0);
  }

  /** 0..1 — how hot is the moment (low timer / hazard near / big combo). Blends gameplay<->tension. */
  setIntensity(v: number): void {
    this.intensity = Phaser.Math.Clamp(v, 0, 1);
    if (this.mode === 'gameplay') this.refreshGameplayTargets();
  }

  private refreshGameplayTargets(): void {
    // Gameplay bed stays mostly present; tension swells in on top as intensity rises.
    this.setLayerTarget('musicGameplay', MUSIC_BED_GAIN * (0.85 + 0.15 * (1 - this.intensity)));
    // S7 combo/chain LIFT floors the tension-layer crossfade higher than the timer/hazard mapping so a
    // hot streak audibly surges the music; it reverts to the intensity curve when the streak breaks.
    const tension = Math.max(0.9 * this.intensity, MUSIC_LIFT_TENSION_FLOOR * this.musicLift);
    this.setLayerTarget('musicTension', MUSIC_BED_GAIN * tension);
    // S7b VILLAIN bassline: BYT3's theme swells in as he pulls ahead (0 when the human leads). tickFade
    // ramps the gain so it fades in/out; the low-pass held-breath + duck cover it (it's on the sub-bus).
    // S9 #35: the standing lead-margin presence + a decaying pulse on each rival score → the villain is
    // audible on every rival point, not only in a blowout.
    const villain = Phaser.Math.Clamp(this.villainPresence + this.villainPulse, 0, 1);
    this.setLayerTarget('musicVillain', MUSIC_BED_GAIN * MUSIC_VILLAIN_MAX * villain);
  }

  /**
   * Set a gameplay music layer's target gain AND re-arm the layer when a rising target would otherwise be
   * inaudible. tickFade auto-stops ANY layer that fades to silence (target 0) to free its source, so a
   * later target RISE on an already-stopped layer would only move a muted gain node — the loop never
   * restarts on its own and the swell is never heard. Whenever a gameplay layer's new target rises above 0
   * while its sound is stopped (or not yet created), ensureLayer restarts it; ensureLayer no-ops a live
   * layer, so this is idempotent and race-free (refreshGameplayTargets runs on the frame update, never
   * nested inside tickFade's iteration). Generalized from the old villain-only guard: the tension layer
   * sits at EXACTLY 0 on a calm board open (intensity 0, lift 0), so its combo/chain LIFT needs the same
   * re-arm or the "lift raises the tension layer" behavior is silent in that state.
   */
  private setLayerTarget(key: MusicKey, target: number): void {
    this.layerTargetGain.set(key, target);
    if (this.mode === 'gameplay' && target > 0) {
      const snd = this.layers.get(key);
      if (!snd || !snd.isPlaying) this.ensureLayer(key);
    }
  }

  private ensureLayer(key: MusicKey): void {
    if (!this.sound) return;
    this.ensureMusicBus();
    let snd = this.layers.get(key);
    if (!snd) {
      try {
        if (!this.sound.game.cache.audio.exists(AUDIO_KEY[key])) return;
        snd = this.sound.add(AUDIO_KEY[key], { loop: true, volume: 0 });
        this.layers.set(key, snd);
        this.routeLayerThroughMusicBus(key, snd);
      } catch {
        return;
      }
    }
    if (!snd.isPlaying) {
      try {
        snd.play({ loop: true, volume: 0 });
      } catch {
        /* context suspended (headless) — it'll start on first gesture; gain ramp handles the rest. */
      }
    }
  }

  private stopLayer(key: MusicKey): void {
    this.layerTargetGain.set(key, 0);
  }

  // --- S7 ADAPTIVE MUSIC DIRECTOR --------------------------------------------
  /**
   * Build the MUSIC sub-bus once: `musicFilter (low-pass) → musicBusGain (breath duck) → master`.
   * Only music layers feed it (routeLayerThroughMusicBus), so SFX/VO/barks are never muffled. Any
   * failure leaves the default graph untouched and marks the bus not-ready → the director no-ops.
   */
  private ensureMusicBus(): void {
    if (this.musicBusReady || !this.sound) return;
    const mgr = this.sound as unknown as {
      context?: AudioContext;
      masterMuteNode?: GainNode;
      masterVolumeNode?: GainNode;
      destination?: AudioNode;
    };
    const ctx = mgr.context;
    const dest = mgr.masterMuteNode ?? mgr.destination ?? mgr.masterVolumeNode;
    if (!ctx || !dest || typeof ctx.createBiquadFilter !== 'function') return;
    try {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = MUSIC_LOWPASS_OPEN_HZ;
      filter.Q.value = 0.7;
      const gain = ctx.createGain();
      gain.gain.value = 1;
      filter.connect(gain);
      gain.connect(dest);
      this.musicFilter = filter;
      this.musicBusGain = gain;
      this.musicBusReady = true;
    } catch {
      this.musicBusReady = false; // default graph stays — director is a silent no-op
    }
  }

  /**
   * Reroute a music layer's output node from the master to the MUSIC sub-bus. A Phaser WebAudioSound
   * wires `... → volumeNode → manager.destination` once at construction; we splice the low-pass in
   * between. The routing persists across loop restarts (only destroy() disconnects volumeNode). On any
   * failure the direct path is restored so the layer still sounds — audio is additive, never fatal.
   */
  private routeLayerThroughMusicBus(key: MusicKey, snd: Phaser.Sound.BaseSound): void {
    if (!this.musicBusReady || !this.musicFilter || this.routedLayers.has(key)) return;
    const ws = snd as unknown as { volumeNode?: AudioNode; pannerNode?: AudioNode | null };
    const out = ws.pannerNode ?? ws.volumeNode; // non-spatial loops → volumeNode
    if (!out) return;
    try {
      out.disconnect(); // its only downstream was manager.destination
      out.connect(this.musicFilter);
      this.routedLayers.add(key);
    } catch {
      try {
        const dest = (this.sound as unknown as { destination?: AudioNode }).destination;
        if (dest) out.connect(dest);
      } catch {
        /* additive — leave it */
      }
    }
  }

  /**
   * HELD-BREATH (armed-traversal): while the human routes an ARMED decoy the music goes muffled +
   * slightly ducked; releasing (CHOMP the thread / leaving the fence) snaps it bright again — the
   * OPEN is the payoff. State-guarded so it schedules a ramp only on a real transition.
   */
  setHeldBreath(on: boolean): void {
    if (on === this.heldBreath) return;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    // #10 threading EXHALE: releasing a held-breath route (thread the CHOMP, or the fence expiring) fires a
    // soft up-whoosh so the payoff is HEARD, not only felt. Gated by a real hold (no 1-frame flicker) + a
    // cooldown so a rapid re-arm can't machine-gun it. Rides the normal SFX path (respects mute + ducking).
    if (!on && this.heldBreathSinceMs > 0 && now - this.heldBreathSinceMs >= THREAD_EXHALE.minHoldMs && now - this.lastExhaleMs >= THREAD_EXHALE.cooldownMs) {
      this.lastExhaleMs = now;
      this.playSfx('threadExhale', { volume: THREAD_EXHALE.gain });
    }
    this.heldBreathSinceMs = on ? now : 0;
    this.heldBreath = on;
    this.applyMusicFilter();
  }

  /**
   * COMBO / CHAIN LIFT: a hot combo (tier ≥3) or a live GAUNTLET CHAIN (≥2 consecutive threads) swells
   * the tension layer ABOVE the timer/hazard intensity mapping — a real "you're on a run" surge that
   * reverts cleanly when the streak breaks. tickFade ramps the layer gain, so the swell never zippers.
   */
  setMusicLift(amount: number): void {
    const v = Phaser.Math.Clamp(amount, 0, 1);
    if (v === this.musicLift) return;
    this.musicLift = v;
    if (this.mode === 'gameplay') this.refreshGameplayTargets();
  }

  /**
   * VILLAIN PRESENCE (S7b): 0..1 how far BYT3 is ahead, driving the villain-bassline layer's swell (0 =
   * human leads/tied → the layer fades to silence; 1 = BYT3 clearly ahead → the layer rides at
   * `MUSIC_BED_GAIN × MUSIC_VILLAIN_MAX`). tickFade ramps the gain so there's no zipper. Renderer feeds
   * this each frame from the rival's live lead margin (musicDirector.ts). No-op unless it actually moved.
   */
  setVillainPresence(amount: number): void {
    const v = Phaser.Math.Clamp(amount, 0, 1);
    if (v === this.villainPresence) return;
    this.villainPresence = v;
    // The re-arm is now central in setLayerTarget (via refreshGameplayTargets): villain presence IS
    // exactly 0 whenever the human leads/ties, so tickFade's "faded to silence → stop to free the source"
    // logic parks the layer; setLayerTarget restarts it when the target rises above 0 as BYT3 pulls ahead,
    // so its gain can actually swell in — the target alone, on a stopped source, would be inaudible.
    if (this.mode === 'gameplay') this.refreshGameplayTargets();
  }

  /**
   * #35 VILLAIN PULSE: BYT3 just scored — give his bassline a short additive swell ON TOP of the standing
   * lead-margin presence, so the villain is heard on every rival point (not only in a blowout). Decays back
   * to the margin-driven presence over ~releaseMs (tickFade). musicDirector calls this on a detected
   * rival-score increase. Rides the same re-arm path as presence, so a stopped villain layer restarts.
   */
  pulseVillain(strength = VILLAIN_PULSE.strength): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    this.villainPulse = Math.max(this.villainPulse, Phaser.Math.Clamp(strength, 0, 1));
    this.villainPulseUntilMs = now + VILLAIN_PULSE.holdMs;
    if (this.mode === 'gameplay') this.refreshGameplayTargets();
  }

  /** Schedule the low-pass + duck-gain ramp toward the current held-breath state (exponential = smooth). */
  private applyMusicFilter(): void {
    if (!this.musicBusReady || !this.musicFilter || !this.musicBusGain || !this.sound) return;
    const ctx = (this.sound as unknown as { context?: AudioContext }).context;
    if (!ctx) return;
    const now = ctx.currentTime;
    const freqTarget = this.heldBreath ? MUSIC_LOWPASS_MUFFLE_HZ : MUSIC_LOWPASS_OPEN_HZ;
    const gainTarget = this.heldBreath ? MUSIC_BREATH_DUCK : 1;
    // Muffle-IN a hair slower than the OPEN: the release is the payoff, so it snaps back bright fast.
    // (Scheduling on a muted/silent graph is inaudible — the feature is naturally a no-op at 0 volume.)
    const tc = this.heldBreath ? 0.05 : 0.022;
    try {
      this.musicFilter.frequency.cancelScheduledValues(now);
      this.musicFilter.frequency.setTargetAtTime(freqTarget, now, tc);
      this.musicBusGain.gain.cancelScheduledValues(now);
      this.musicBusGain.gain.setTargetAtTime(gainTarget, now, tc);
    } catch {
      /* AudioParam scheduling unsupported — leave the filter as-is (additive) */
    }
  }

  /** Snap the director back to neutral on any music-mode switch (open filter, drop lift). */
  private resetMusicDirector(): void {
    this.heldBreath = false;
    this.heldBreathSinceMs = 0; // #10 no stale exhale carries across a mode switch
    this.musicLift = 0;
    this.villainPresence = 0;
    this.villainPulse = 0; // #35 the villain pulse never bleeds into the menu / next board
    this.villainPulseUntilMs = 0;
    this.applyMusicFilter();
  }

  /** S7 evidence hook: the live music-director state (reads the actual AudioParam values). */
  musicDirectorState(): {
    ready: boolean;
    heldBreath: boolean;
    lift: number;
    villainPresence: number;
    filterHz: number;
    busGain: number;
    tensionTarget: number;
    tensionGain: number;
    gameplayTarget: number;
    villainTarget: number;
    villainGain: number;
    duckLevel: number;
    duckAmount: number;
    duckActive: boolean;
  } {
    const villain = this.layers.get('musicVillain') as WebAudioSound | undefined;
    const tension = this.layers.get('musicTension') as WebAudioSound | undefined;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return {
      ready: this.musicBusReady,
      heldBreath: this.heldBreath,
      lift: this.musicLift,
      villainPresence: Number(this.villainPresence.toFixed(3)),
      filterHz: this.musicFilter ? Math.round(this.musicFilter.frequency.value) : -1,
      busGain: this.musicBusGain ? Number(this.musicBusGain.gain.value.toFixed(3)) : -1,
      tensionTarget: Number((this.layerTargetGain.get('musicTension') ?? 0).toFixed(4)),
      // ACTUAL live volume of the tension layer (like villainGain) — proves the LIFT is audible, not just
      // targeted: a stopped/parked layer reads ~0 here even with a positive tensionTarget until it re-arms.
      tensionGain: tension && typeof tension.volume === 'number' ? Number(tension.volume.toFixed(4)) : -1,
      gameplayTarget: Number((this.layerTargetGain.get('musicGameplay') ?? 0).toFixed(4)),
      villainTarget: Number((this.layerTargetGain.get('musicVillain') ?? 0).toFixed(4)),
      villainGain: villain && typeof villain.volume === 'number' ? Number(villain.volume.toFixed(4)) : -1,
      duckLevel: Number(this.duckLevel.toFixed(3)),
      duckAmount: Number(this.duckAmount.toFixed(3)),
      duckActive: now < this.duckHoldUntilMs,
    };
  }

  /** Per-frame gain ramp toward each layer's target. Cheap; runs only while a scene is alive. */
  private tickFade(_t: number, delta: number): void {
    if (!this.sound || this.layers.size === 0) return;
    const step = Phaser.Math.Clamp(delta / 600, 0, 1); // ~0.6s full crossfade
    // Ducking envelope: fast attack toward duckAmount while held, ~0.5s release back to unity.
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    // #35 decay the villain pulse toward 0 once its hold expires, then re-resolve the villain target so the
    // swell rides down smoothly. refreshGameplayTargets runs HERE, before the per-layer loop below — never
    // nested inside it (setLayerTarget's re-arm contract).
    if (this.villainPulse > 0 && now >= this.villainPulseUntilMs) {
      this.villainPulse += (0 - this.villainPulse) * Phaser.Math.Clamp(delta / VILLAIN_PULSE.releaseMs, 0, 1);
      if (this.villainPulse < 0.002) this.villainPulse = 0;
      if (this.mode === 'gameplay') this.refreshGameplayTargets();
    }
    const duckTarget = now < this.duckHoldUntilMs ? this.duckAmount : 1;
    const duckStep = duckTarget < this.duckLevel ? Phaser.Math.Clamp(delta / 90, 0, 1) : Phaser.Math.Clamp(delta / 500, 0, 1);
    this.duckLevel += (duckTarget - this.duckLevel) * duckStep;
    if (duckTarget === 1 && this.duckLevel > 0.995) this.duckLevel = 1;
    const master = this.masterVolume * this.musicBusMul * this.duckLevel * (this.bedDim ? 0.4 : 1);
    for (const [key, snd] of this.layers) {
      const target = (this.layerTargetGain.get(key) ?? 0) * master;
      const ws = snd as WebAudioSound;
      const cur = typeof ws.volume === 'number' ? ws.volume : 0;
      const next = cur + (target - cur) * step;
      try {
        ws.setVolume(next);
      } catch {
        /* NoAudio / not yet decoded */
      }
      // Once a stopped layer has faded to silence, actually stop it to free the source.
      if (target <= 0.0005 && next <= 0.002 && snd.isPlaying) {
        try {
          snd.stop();
        } catch {
          /* ignore */
        }
      }
    }
  }

  /** Resume a context that the browser suspended until the first gesture (call from input). */
  unlock(): void {
    try {
      const ctx = (this.sound as Phaser.Sound.WebAudioSoundManager | null)?.context;
      if (ctx && ctx.state === 'suspended') void ctx.resume();
    } catch {
      /* not a WebAudio manager */
    }
  }

  destroy(): void {
    this.unsub?.();
    this.unsub = null;
  }
}

let bus: AudioBus | null = null;
/** Singleton accessor — one bus for the whole app lifetime. */
export function audio(): AudioBus {
  if (!bus) bus = new AudioBus();
  return bus;
}

// =============================================================================
// AUDIO-CAPTURE TEE (?audiocap=1) — capture-harness-only real-mix recorder.
//
// The gameplay-video gate records the Phaser canvas via Playwright, which has NO audio track — every
// review clip was silent. This tee lets tools/capture-gameplay-clip.mjs (AUDIO=1) record the game's
// REAL master mix in-page: it connects Phaser's final master gain (chain: sounds → masterMuteNode →
// masterVolumeNode → context.destination) ADDITIONALLY into a MediaStreamAudioDestinationNode — the
// normal speaker path is never disconnected — and records that stream with MediaRecorder
// (audio/webm;codecs=opus). The tool pulls the blob as base64 and ffmpeg-muxes it with the video.
//
// Production-safe: installed ONLY when the page URL carries ?audiocap=1 (appended by the capture
// tool; no shipped surface uses it), zero cost otherwise, and never throws — audio stays additive.
// Headless note: start() resumes a suspended AudioContext; the tool launches Chromium with
// --autoplay-policy=no-user-gesture-required so the context runs without a user gesture.
// =============================================================================

interface AudioCaptureStopResult {
  ok: boolean;
  /** the full recording (opus/webm) as base64 — ~1MB/min at 128kbps, safe through page.evaluate */
  base64: string | null;
  bytes: number;
  mimeType: string | null;
  startedAtMs: number | null;
  stoppedAtMs: number | null;
  error: string | null;
}

export interface AudioCaptureHook {
  ready: boolean;
  method: string;
  mimeType: string | null;
  contextState(): string;
  /**
   * Begin recording. Returns Date.now() sampled immediately AFTER MediaRecorder.start() (the tool's
   * AV-alignment anchor; recorder spin-up leaves a small ±100ms residual). null = failed to start.
   * Idempotent: returns the original start time if already recording.
   */
  start(timesliceMs?: number): number | null;
  /** Stop and return the whole recording. Safe to call when not recording (ok:false, empty). */
  stop(): Promise<AudioCaptureStopResult>;
}

type AudioCaptureWindow = { __audiocap?: AudioCaptureHook };

function captureTeeRequested(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('audiocap') === '1';
  } catch {
    return false; // no window/location (tests) — never install
  }
}

// Master limiter node (when installed) — module-level so the capture tee taps POST-limiter and the
// recording carries the same protected mix the player hears.
let masterLimiter: DynamicsCompressorNode | null = null;

/**
 * Insert a brickwall-ish compressor between Phaser's master gain and the context destination
 * (threshold −6 dB, ratio 20:1, 2 ms attack) so stacked set-piece one-shots (VO + riser + fanfare)
 * can no longer push true peaks past 0 dBFS (first-listen review MAJOR: audible clipping @16 s).
 * Structural access; on any failure the graph is left untouched — audio is additive, never fatal.
 */
function installMasterLimiter(sound: Phaser.Sound.BaseSoundManager): void {
  if (masterLimiter) return;
  const mgr = sound as unknown as {
    context?: AudioContext;
    masterVolumeNode?: GainNode;
    masterMuteNode?: GainNode;
  };
  const ctx = mgr.context;
  const tap = mgr.masterVolumeNode ?? mgr.masterMuteNode;
  if (!ctx || !tap || typeof ctx.createDynamicsCompressor !== 'function') return;
  try {
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 4;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.12;
    tap.disconnect(); // its only downstream is context.destination
    tap.connect(limiter);
    limiter.connect(ctx.destination);
    masterLimiter = limiter;
  } catch {
    /* leave the default graph in place */
  }
}

function installCaptureTee(sound: Phaser.Sound.BaseSoundManager): void {
  const w = window as unknown as AudioCaptureWindow;
  if (w.__audiocap) return;
  // Structural access: only the WebAudio manager has these; NoAudio/HTML5 managers can't tee (the
  // capture tool then reports has_audio:false honestly instead of faking a mix).
  const mgr = sound as unknown as {
    context?: AudioContext;
    masterVolumeNode?: GainNode;
    masterMuteNode?: GainNode;
  };
  const ctx = mgr.context;
  // Prefer the post-limiter node: the recording must carry the protected mix the player hears.
  const tap: AudioNode | undefined = masterLimiter ?? mgr.masterVolumeNode ?? mgr.masterMuteNode;
  if (
    !ctx ||
    !tap ||
    typeof ctx.createMediaStreamDestination !== 'function' ||
    typeof MediaRecorder === 'undefined'
  ) {
    return;
  }
  let streamDest: MediaStreamAudioDestinationNode;
  try {
    streamDest = ctx.createMediaStreamDestination();
    tap.connect(streamDest); // ADDITIVE tee — context.destination stays connected
  } catch {
    return;
  }
  const preferred = 'audio/webm;codecs=opus';
  const mimeType =
    typeof MediaRecorder.isTypeSupported !== 'function' || MediaRecorder.isTypeSupported(preferred)
      ? preferred
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : null;

  let rec: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let startedAtMs: number | null = null;

  w.__audiocap = {
    ready: true,
    method: 'webaudio-master-tee+mediarecorder',
    mimeType,
    contextState: () => ctx.state,
    start(timesliceMs = 1000): number | null {
      if (rec && rec.state !== 'inactive') return startedAtMs; // already recording
      if (ctx.state !== 'running') void ctx.resume().catch(() => undefined);
      try {
        chunks = [];
        rec = new MediaRecorder(
          streamDest.stream,
          mimeType ? { mimeType, audioBitsPerSecond: 128_000 } : undefined,
        );
        rec.ondataavailable = (ev: BlobEvent): void => {
          if (ev.data && ev.data.size > 0) chunks.push(ev.data);
        };
        rec.start(timesliceMs);
        startedAtMs = Date.now();
        return startedAtMs;
      } catch {
        rec = null;
        return null;
      }
    },
    stop(): Promise<AudioCaptureStopResult> {
      return new Promise((resolve) => {
        const finish = (): void => {
          const stoppedAtMs = Date.now();
          const blob = new Blob(chunks, { type: mimeType ?? 'audio/webm' });
          chunks = [];
          if (blob.size === 0) {
            resolve({
              ok: false,
              base64: null,
              bytes: 0,
              mimeType,
              startedAtMs,
              stoppedAtMs,
              error: 'empty recording (recorder never started or context suspended)',
            });
            return;
          }
          const reader = new FileReader();
          reader.onloadend = (): void => {
            const url = typeof reader.result === 'string' ? reader.result : '';
            const b64 = url.includes(',') ? url.slice(url.indexOf(',') + 1) : '';
            resolve({
              ok: b64.length > 0,
              base64: b64 || null,
              bytes: blob.size,
              mimeType,
              startedAtMs,
              stoppedAtMs,
              error: b64 ? null : 'base64 encode failed',
            });
          };
          reader.onerror = (): void =>
            resolve({
              ok: false,
              base64: null,
              bytes: blob.size,
              mimeType,
              startedAtMs,
              stoppedAtMs,
              error: 'blob read failed',
            });
          reader.readAsDataURL(blob);
        };
        if (!rec || rec.state === 'inactive') {
          finish();
          return;
        }
        rec.onstop = finish;
        try {
          rec.stop();
        } catch {
          finish();
        }
      });
    },
  };
}
